import { Check, X } from 'lucide-react';
import { useInventory } from '@/arche/arches/dfe/hooks/useInventory';
import { cn } from '@/lib/utils';
import type { LLCheckInventoryElement } from '../../types';

/**
 * Renders an inventory requirement and shows live ✓/✗ based on the player's
 * current inventory. Read-only consumer — the platform DFE inventory is the
 * source of truth, and the server re-checks on submit.
 *
 * Uses the existing dfe-arche `useInventory` hook with a search filter. React
 * Query dedupes identical queryKeys, so two requirements for the same item
 * issue one network request.
 */
export function CheckInventoryElement({ element }: { element: LLCheckInventoryElement }) {
  const { items, loading } = useInventory({ search: element.item });

  // Fungible items contribute their balance quantity; non-fungible items each
  // count as 1 instance. Server-side validator does the same aggregation.
  const owned = items
    .filter((i) => i.name.toLowerCase() === element.item.toLowerCase())
    .reduce((sum, i) => sum + (i.quantity ?? 1), 0);
  const satisfied = !loading && owned >= element.amount;

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        loading && 'opacity-70',
        !loading && satisfied && 'border-green-500/40 bg-green-500/5',
        !loading && !satisfied && 'border-amber-500/40 bg-amber-500/5',
      )}
    >
      <span aria-hidden className="mt-0.5">
        {loading ? (
          <span className="inline-block h-4 w-4 animate-pulse rounded-full bg-muted-foreground/30" />
        ) : satisfied ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <X className="h-4 w-4 text-amber-600" />
        )}
      </span>
      <div className="flex-1">
        <p className="font-medium">{element.description}</p>
        <p className="text-xs text-muted-foreground">
          Required: {element.amount} {element.item}
          {!loading && ` — you have ${owned}`}
        </p>
      </div>
    </div>
  );
}
